import React from 'react';
import { Button, Input, Table, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import qs from 'query-string';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ConfirmationType, TagListType } from '../../../../../../shared/types';
import EmptyState from '../../../../../../shared/components/EmptyState';
import {
  Center,
  FormContainer,
  FormContent,
  FormHeader,
  StyledTags,
} from '../../../../../../shared/styles';
import {
  useDeletetagList,
  useGetTagCount,
  useGetTagLists,
  useSaveTagList,
} from '../hooks';
import { TablePane } from './styles';
import { TAGS_PER_PAGE } from '../constants';

type TagsProps = {
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

const Tags = (props: TagsProps) => {
  const { t } = useTranslation();
  const [selectedRowIds, setSelectedRowIds] = React.useState<
    Record<string, boolean>
  >({});
  const [pageToken, setPageToken] = React.useState<any>({ 0: '' });
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [tagLists, setTagLists] = React.useState<any>();

  const {
    isFetching: isFetchingTags,
    isLoading: isLoadingTags,
    refetch: refetchAllTags,
    isError: isTagError,
  } = useGetTagLists({
    pageToken: pageToken[pageIndex] || '',
    callback: async (isSuccess: boolean, response: any) => {
      if (isSuccess) {
        setTagLists(response?.accountTags || []);
        const nextURL = get(response, ['_links', 'next', 'href'], '');
        if (nextURL) {
          const parseData: any = qs.parseUrl(nextURL);
          if (parseData.query?.next_token) {
            setPageToken((pgTkn: any) => ({
              ...pgTkn,
              [pageIndex + 1]: parseData.query.next_token,
            }));
          }
        }
      }
    },
  });
  const { data: tagsCount = 0 } = useGetTagCount({});

  const [selectedTag, setSelectedTag] = React.useState<TagListType>();
  const [, setToast] = useToasts();
  const { setConfirmationData } = props;

  const { mutate: savetagList, isLoading: isSavingTagList } = useSaveTagList({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setPageIndex(0);
        setSelectedTag(undefined);
      }
    },
  });
  const { mutate: deleteTag } = useDeletetagList({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setPageIndex(0);
        setConfirmationData({});
      }
    },
  });

  const onDeleteTag = (tagToDelete: TagListType) => {
    setConfirmationData({
      id: new Date().toISOString(),
      title: `${t('confirm_deletion')}`,
      onConfirm: () => {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: true,
        }));
        deleteTag(tagToDelete);
      },
      confirmButtonText: `${t('delete')}`,
      className: 'error',
      fill: '#d7461f',
      infoText: tagToDelete?.tag_response_count
        ? `${t('settings.tags.delete_tag.info_text')}`
        : '',
      description: `${t('settings.tags.delete_tag.description')}`,
      glyph: 'info',
    });
  };
  // const tagNameRegex = /^([a-zA-Z0-9]+[-_a-zA-Z0-9\s]*[a-zA-Z0-9])+$/i;
  const tagNameRegex = /^([a-zA-Z0-9]+\s)*[-_a-zA-Z0-9]+$/i;

  const form = useForm({
    resolver: yupResolver(
      Yup.object({
        created_by_id: Yup.string().nullable(true),
        id: Yup.string().nullable(true),
        name: Yup.string()
          .required(`${t('forms.err_msg.tag_name_required')}`)
          .trim()
          .max(
            90,
            `${t('forms.err_msg.max_char', {
              len: '90',
            })}`,
          )
          .min(
            2,
            `${t('forms.err_msg.min_char', {
              len: '2',
            })}`,
          )
          .matches(tagNameRegex, `${t('forms.err_msg.valid_tag_name')}`),
      }).required(),
    ),
    defaultValues: {
      name: selectedTag ? selectedTag?.name : '',
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  React.useEffect(() => {
    refetchAllTags();
  }, [pageIndex]);

  React.useEffect(() => {
    if (selectedTag) reset({ ...selectedTag });
  }, [selectedTag]);

  const onSubmit = (rowdata: TagListType) => {
    const updated = {
      ...rowdata,
      case_insensitive_name: rowdata.name,
    };
    savetagList(updated);
  };

  const columns = React.useMemo(() => {
    return [
      {
        Header: 'Tag Name',
        accessor: (rowdata: TagListType) => {
          return rowdata.id === selectedTag?.id ? (
            <>
              <Input
                placeholder={`${t('forms.placeholders.enter_tag')}`}
                autoFocus
                isRequired
                isErrored={Boolean(errors?.name?.message)}
                errorText={errors?.name?.message}
                {...register('name')}
              />
            </>
          ) : (
            <div>{rowdata.name}</div>
          );
        },
      },
      {
        Header: 'Number of Responses',
        accessor: (rowdata: TagListType) => {
          return rowdata?.tag_response_count || '-';
        },
        disableSortBy: true,
      },
      {
        Header: '',
        id: 'actions',
        accessor: (rowdata: TagListType) => {
          return rowdata.id === selectedTag?.id ? (
            <div style={{ textAlign: 'right', padding: '5px 0 5px 0' }}>
              <Button inline type="primary" disabled={isSavingTagList}>
                {t('save')}
              </Button>
              <Button
                inline
                type="secondary"
                onClick={() => setSelectedTag(undefined)}
              >
                {t('cancel')}
              </Button>
            </div>
          ) : null;
        },
      },
    ];
  }, [selectedTag, isSavingTagList, errors]);

  if (isTagError)
    return (
      <Center minHeight="calc(100vh - 136px)">
        {t('empty_state.message.network_error')}
      </Center>
    );

  return (
    <StyledTags
      isNotEmpty={
        (tagLists?.length && !isLoadingTags && !isFetchingTags) || false
      }
    >
      <FormContainer
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit, (err) => console.log('err', err))(e);
        }}
      >
        <FormHeader style={{ borderBottom: 'none' }}>
          <div className="left-pane">
            <div className="heading">
              {t('settings.tags.title')}&nbsp;
              <span>{tagsCount ? <span>{`(${tagsCount})`}</span> : null}</span>
            </div>
          </div>
          <div className="right-pane">
            <Button
              inline
              disabled={isFetchingTags}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setSelectedTag({ id: '', name: '' });
              }}
            >
              {t('settings.tags.add_tag')}
            </Button>
          </div>
        </FormHeader>
        <FormContent pd="0 24px">
          <TablePane>
            <Table
              dataKey="id"
              data={
                selectedTag
                  ? [selectedTag, ...(tagLists ?? [])]
                  : tagLists || []
              }
              count={tagsCount}
              columns={columns}
              pageSize={TAGS_PER_PAGE}
              isLoading={!tagLists || isLoadingTags || isFetchingTags}
              selectedRowIds={selectedRowIds}
              loaderStyles={{ minHeight: 'calc(100vh - 250px)' }}
              setSelectedRowIds={setSelectedRowIds}
              disableRowSelect
              hideFilters
              hideBottomPagination
              hideTableOnEmpty
              onRowClick={(rowData: any, columnData: any) => {
                if (columnData.id === 'name') {
                  setSelectedTag(rowData.original);
                }
              }}
              isFetching={isFetchingTags}
              pageIndex={pageIndex}
              setPageIndex={(pgNo: number) => setPageIndex(pgNo)}
              onMenuOptionSelect={(option: string, rowData: TagListType) => {
                switch (option) {
                  case 'edit':
                    setSelectedTag(rowData);
                    break;
                  case 'delete':
                    onDeleteTag(rowData);
                    break;
                }
              }}
              rowMenuOptions={(rowdata: TagListType) =>
                rowdata.id !== selectedTag?.id
                  ? [
                      {
                        id: 'edit',
                        name: t('edit'),
                        disabled: selectedTag ? true : false,
                      },
                      {
                        id: 'delete',
                        name: t('delete'),
                        disabled: selectedTag ? true : false,
                      },
                    ]
                  : []
              }
              emptyComponent={
                <EmptyState
                  title={`${t('empty_state.title.no_tags_to_display')}`}
                  message={
                    isTagError
                      ? `${t('empty_state.message.network_error')}`
                      : `${t('empty_state.message.click_to_add_tags')}`
                  }
                  imgSrc="empty-state-survey.svg"
                  minHeight="400px"
                >
                  <Button
                    inline
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setSelectedTag({ id: '', name: '' });
                    }}
                  >
                    {t('settings.tags.add_tag')}
                  </Button>
                </EmptyState>
              }
            />
          </TablePane>
        </FormContent>
      </FormContainer>
    </StyledTags>
  );
};

export default Tags;
